import Dexie from 'dexie';
import { Html5Qrcode } from 'html5-qrcode';
import QRCode from 'qrcode';
import * as XLSX from 'xlsx';

const db = new Dexie('BeyondUniAttendanceDB');
db.version(1).stores({
  volunteers: '&id,name,team,updatedAt',
  attendance: '&key,date,volunteerId,status,[date+status],lastModified',
  settings: '&key',
  outbox: '&id,synced,createdAt',
  backups: '++id,createdAt'
});

const DEFAULTS = {
  campaignDates: ['2026-07-18', '2026-07-19', '2026-07-20'],
  campaignStart: '16:00',
  lateThreshold: 15,
  pinHash: '',
  syncEndpoint: '/api/sync',
  adminToken: '',
  lastBackupAt: ''
};

const state = {
  settings: { ...DEFAULTS },
  currentPage: 'scanner',
  supervisorUnlocked: false,
  selectedVolunteer: null,
  scanner: null,
  scanning: false,
  lastScanValue: '',
  lastScanAt: 0,
  pendingPinAction: null,
  editingVolunteerPhoto: '',
  currentQrVolunteer: null,
  reportRows: [],
  reportHeaders: [],
  deferredInstallPrompt: null,
  backupTimer: null,
  syncTimer: null,
  syncing: false
};

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];
const isoNow = () => new Date().toISOString();
const recordKey = (date, volunteerId) => `${date}|${volunteerId}`;
const normalizeId = (value) => String(value ?? '').trim().replace(/\s+/g, '-').toUpperCase();
const safeText = (value) => String(value ?? '');

function escapeHtml(value) {
  return safeText(value).replace(/[&<>'"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[ch]));
}

function todayIso() {
  return new Date().toLocaleDateString('en-CA');
}

function displayDate(date) {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${date}T12:00:00`));
}

function displayTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
}

function displayDuration(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return '—';
  const minutes = Math.max(0, Math.round(Number(hours) * 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function initials(name) {
  return safeText(name).trim().split(/\s+/).slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || 'BU';
}

function statusLabel(record) {
  if (!record) return 'Absent';
  if (record.status === 'checked_in') return record.late ? 'Checked in · Late' : 'Checked in';
  if (record.status === 'checked_out') return record.late ? 'Checked out · Late' : 'Checked out';
  if (record.status === 'absent') return 'Absent';
  return 'Not recorded';
}

function statusClass(record) {
  if (!record) return 'absent';
  return record.status || 'neutral';
}

function nowForCampaignDate(date) {
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  return new Date(`${date}T${time}`).toISOString();
}

function timeToDate(date, time) {
  if (!date || !time) return '';
  return new Date(`${date}T${time}:00`).toISOString();
}

function dateToTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function calcHours(checkIn, checkOut) {
  if (!checkIn || !checkOut) return null;
  return Math.max(0, (new Date(checkOut) - new Date(checkIn)) / 3600000);
}

function isLate(checkIn, date) {
  if (!checkIn) return false;
  const start = new Date(`${date}T${state.settings.campaignStart}:00`);
  start.setMinutes(start.getMinutes() + Number(state.settings.lateThreshold || 0));
  return new Date(checkIn) > start;
}

function randomId() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = 'BU-';
  for (let i = 0; i < 6; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
}

function randomToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
}

async function sha256(value) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}

async function settingGet(key, fallback) {
  const row = await db.settings.get(key);
  return row ? row.value : fallback;
}

async function settingSet(key, value) {
  await db.settings.put({ key, value });
  state.settings[key] = value;
}

async function loadSettings() {
  for (const [key, fallback] of Object.entries(DEFAULTS)) {
    state.settings[key] = await settingGet(key, fallback);
  }
  if (!state.settings.adminToken) {
    state.settings.adminToken = randomToken();
    await settingSet('adminToken', state.settings.adminToken);
  }
  if (!state.settings.syncEndpoint && location.protocol !== 'file:') {
    await settingSet('syncEndpoint', '/api/sync');
  }
}

function toast(message, type = '') {
  const item = document.createElement('div');
  item.className = `toast ${type}`.trim();
  item.textContent = message;
  $('#toastContainer').appendChild(item);
  setTimeout(() => item.remove(), 3600);
}

function setScannerMessage(message, type = 'info') {
  const el = $('#scannerMessage');
  el.className = `message ${type}`;
  el.textContent = message;
}

function setConnectionBadge() {
  const badge = $('#connectionBadge');
  const text = badge.querySelector('span:last-child');
  badge.className = 'status-pill';
  if (!navigator.onLine) {
    badge.classList.add('status-offline');
    text.textContent = 'Offline · Saved locally';
  } else if (state.settings.syncEndpoint) {
    text.textContent = 'Online';
  } else {
    badge.classList.add('status-local');
    text.textContent = 'Online · Local only';
  }
}

function populateDateSelects() {
  const dates = [...new Set((state.settings.campaignDates || []).filter(Boolean))].sort();
  if (!dates.length) dates.push(todayIso());
  const target = dates.includes(todayIso()) ? todayIso() : dates[0];
  for (const id of ['scannerDate', 'dashboardDate', 'manualDate', 'reportDate']) {
    const select = $(`#${id}`);
    const previous = select.value;
    select.innerHTML = dates.map(date => `<option value="${escapeHtml(date)}">${escapeHtml(displayDate(date))}</option>`).join('');
    select.value = dates.includes(previous) ? previous : target;
  }
  updateCampaignDateLabel();
}

function updateCampaignDateLabel() {
  const selected = $('#scannerDate')?.value || todayIso();
  $('#campaignDateLabel').textContent = displayDate(selected);
}

function renderTeamOptions(teams) {
  const unique = [...new Set(teams.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  for (const id of ['dashboardTeam', 'volunteerTeam', 'reportTeam']) {
    const select = $(`#${id}`);
    const old = select.value;
    const label = id === 'reportTeam' || id === 'dashboardTeam' || id === 'volunteerTeam' ? 'All teams' : 'All';
    select.innerHTML = `<option value="">${label}</option>${unique.map(team => `<option value="${escapeHtml(team)}">${escapeHtml(team)}</option>`).join('')}`;
    select.value = unique.includes(old) ? old : '';
  }
}

async function refreshTeamOptions() {
  const volunteers = await db.volunteers.toArray();
  renderTeamOptions(volunteers.map(v => v.team));
}

async function queueEvent(type, payload) {
  await db.outbox.put({
    id: crypto.randomUUID(),
    type,
    payload,
    createdAt: isoNow(),
    synced: 0
  });
  await maybeAutoBackup();
  if (navigator.onLine && state.settings.syncEndpoint) {
    clearTimeout(state.syncTimer);
    state.syncTimer = setTimeout(() => syncOutbox(false), 500);
  }
}

async function applyCloudSnapshot(snapshot) {
  if (!snapshot) return;
  const cloudVolunteers = Array.isArray(snapshot.volunteers) ? snapshot.volunteers : [];
  const cloudAttendance = Array.isArray(snapshot.attendance) ? snapshot.attendance : [];
  const deletedVolunteers = Array.isArray(snapshot.deletedVolunteers) ? snapshot.deletedVolunteers : [];
  const deletedAttendance = Array.isArray(snapshot.deletedAttendance) ? snapshot.deletedAttendance : [];

  await db.transaction('rw', db.volunteers, db.attendance, async () => {
    for (const id of deletedVolunteers) await db.volunteers.delete(id);
    for (const key of deletedAttendance) await db.attendance.delete(key);

    for (const item of cloudVolunteers) {
      const local = await db.volunteers.get(item.id);
      if (!local || String(item.updatedAt || '') >= String(local.updatedAt || '')) await db.volunteers.put(item);
    }
    for (const item of cloudAttendance) {
      const local = await db.attendance.get(item.key);
      if (!local || String(item.lastModified || '') >= String(local.lastModified || '')) await db.attendance.put(item);
    }
  });
  await refreshTeamOptions();
}

async function syncOutbox(showFeedback = true) {
  if (state.syncing) return;
  setConnectionBadge();
  const status = $('#syncStatus');
  if (!navigator.onLine) {
    status.textContent = 'Offline. Changes are saved locally and will remain queued.';
    if (showFeedback) toast('Offline: records are saved locally.', 'warning');
    return;
  }
  if (!state.settings.syncEndpoint) {
    status.textContent = 'Saved locally. No sync endpoint is configured.';
    if (showFeedback) toast('No sync endpoint configured. Data is safe on this device.', 'warning');
    return;
  }

  const pending = await db.outbox.where('synced').equals(0).toArray();
  status.textContent = pending.length
    ? `Synchronizing ${pending.length} local change${pending.length === 1 ? '' : 's'}…`
    : 'Checking cloud database…';
  state.syncing = true;
  try {
    const response = await fetch(state.settings.syncEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: 'BeyondUni Attendance Tracker', events: pending })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Server responded ${response.status}`);
    await applyCloudSnapshot(data);
    if (pending.length) {
      await db.outbox.bulkPut(pending.map(item => ({ ...item, synced: 1, syncedAt: isoNow() })));
    }
    status.textContent = `Synchronized successfully · ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    setConnectionBadge();
    if (state.currentPage === 'dashboard') await renderDashboard();
    if (state.currentPage === 'manual') await renderManualResults();
    if (state.currentPage === 'volunteers') await renderVolunteers();
    if (state.currentPage === 'reports') await renderReport();
    if (showFeedback) toast('Cloud synchronization completed.', 'success');
  } catch (error) {
    status.textContent = `Saved locally. Synchronization failed: ${error.message}`;
    if (showFeedback) toast('Synchronization failed. Records remain saved locally.', 'error');
  } finally {
    state.syncing = false;
  }
}

function unlockSupervisor(showToast = true) {
  state.supervisorUnlocked = true;
  $('#mainNav').classList.remove('locked');
  if (showToast) toast('Supervisor tools unlocked.', 'success');
}

function lockSupervisor() {
  state.supervisorUnlocked = false;
  $('#mainNav').classList.add('locked');
  navigate('scanner');
  toast('Supervisor tools hidden.');
}

function navigate(page) {
  if (page !== 'scanner' && !state.supervisorUnlocked) return;
  state.currentPage = page;
  $$('.page').forEach(el => el.classList.toggle('active', el.id === `page-${page}`));
  $$('.nav-btn').forEach(el => el.classList.toggle('active', el.dataset.page === page));
  window.scrollTo({ top: 0, behavior: 'smooth' });
  if (page === 'dashboard') renderDashboard();
  if (page === 'manual') renderManualResults();
  if (page === 'volunteers') renderVolunteers();
  if (page === 'reports') renderReport();
  if (page === 'settings') renderSettings();
}

async function requirePin(action) {
  if (!state.settings.pinHash) {
    await action();
    return;
  }
  state.pendingPinAction = action;
  $('#pinInput').value = '';
  $('#pinError').classList.add('hidden');
  $('#pinDialog').showModal();
  setTimeout(() => $('#pinInput').focus(), 50);
}

async function getVolunteerRecord(id, date) {
  const volunteer = await db.volunteers.get(normalizeId(id));
  const attendance = volunteer ? await db.attendance.get(recordKey(date, volunteer.id)) : null;
  return { volunteer, attendance };
}

function avatarHtml(volunteer, className = 'mini-avatar') {
  return volunteer.photo
    ? `<span class="${className}"><img src="${escapeHtml(volunteer.photo)}" alt="" /></span>`
    : `<span class="${className}">${escapeHtml(initials(volunteer.name))}</span>`;
}

function hideScannerLookupResults() {
  const results = $('#scannerLookupResults');
  results.innerHTML = '';
  results.classList.add('hidden');
}

async function selectVolunteerForScanner(volunteer, inputValue = '') {
  if (!volunteer) return;
  const date = $('#scannerDate').value;
  const attendance = await db.attendance.get(recordKey(date, volunteer.id));
  state.selectedVolunteer = volunteer;
  if (inputValue) $('#manualScanId').value = inputValue;
  hideScannerLookupResults();
  renderScanResult(volunteer, attendance, date);
  navigator.vibrate?.(80);
}

function renderScannerLookupResults(volunteers, query) {
  const results = $('#scannerLookupResults');
  if (!volunteers.length) {
    hideScannerLookupResults();
    return;
  }
  results.innerHTML = `
    <p class="scanner-lookup-heading">${volunteers.length === 1 ? 'Select volunteer' : `Multiple matches for “${escapeHtml(query)}”. Select the correct volunteer:`}</p>
    ${volunteers.map(volunteer => `
      <button class="scanner-lookup-option" type="button" data-volunteer-id="${escapeHtml(volunteer.id)}">
        <span class="scanner-lookup-person">
          ${avatarHtml(volunteer)}
          <span><strong>${escapeHtml(volunteer.name)}</strong><small>${escapeHtml(volunteer.team || 'No team / role')}</small></span>
        </span>
        <span class="scanner-lookup-id">${escapeHtml(volunteer.id)}</span>
      </button>`).join('')}
  `;
  results.classList.remove('hidden');
}

async function findVolunteersByNameOrId(value) {
  const query = safeText(value).trim();
  if (!query) return [];
  const normalizedQuery = normalizeId(query);
  const exactId = await db.volunteers.get(normalizedQuery);
  if (exactId) return [exactId];

  const lowerQuery = query.toLocaleLowerCase();
  const queryWords = lowerQuery.split(/\s+/).filter(Boolean);
  const volunteers = await db.volunteers.toArray();
  return volunteers
    .map(volunteer => {
      const name = safeText(volunteer.name).trim().toLocaleLowerCase();
      const id = safeText(volunteer.id).toLocaleLowerCase();
      let score = Number.POSITIVE_INFINITY;
      if (name === lowerQuery) score = 0;
      else if (name.startsWith(lowerQuery)) score = 1;
      else if (queryWords.length && queryWords.every(word => name.includes(word))) score = 2;
      else if (name.includes(lowerQuery)) score = 3;
      else if (id.startsWith(lowerQuery) || id.includes(lowerQuery)) score = 4;
      return { volunteer, score };
    })
    .filter(item => Number.isFinite(item.score))
    .sort((a, b) => a.score - b.score || a.volunteer.name.localeCompare(b.volunteer.name))
    .slice(0, 20)
    .map(item => item.volunteer);
}

async function handleManualVolunteerLookup(value) {
  const query = safeText(value).trim();
  if (!query) {
    hideScannerLookupResults();
    setScannerMessage('Enter a volunteer name or volunteer ID.', 'error');
    return;
  }

  const matches = await findVolunteersByNameOrId(query);
  if (!matches.length) {
    state.selectedVolunteer = null;
    $('#scanResultCard').classList.add('hidden');
    hideScannerLookupResults();
    setScannerMessage(`No volunteer found for “${query}”. Check the spelling or add the volunteer first.`, 'error');
    navigator.vibrate?.([120, 60, 120]);
    return;
  }

  const exactNameMatches = matches.filter(volunteer => safeText(volunteer.name).trim().toLocaleLowerCase() === query.toLocaleLowerCase());
  if (matches.length === 1 || exactNameMatches.length === 1) {
    const volunteer = exactNameMatches[0] || matches[0];
    await selectVolunteerForScanner(volunteer, volunteer.name);
    return;
  }

  state.selectedVolunteer = null;
  $('#scanResultCard').classList.add('hidden');
  renderScannerLookupResults(matches, query);
  setScannerMessage(`${matches.length} volunteers found. Select the correct person below.`, 'info');
}

async function handleScannedValue(value) {
  const id = normalizeId(value);
  const now = Date.now();
  if (!id) {
    setScannerMessage('Invalid QR code. The code does not contain a volunteer ID.', 'error');
    return;
  }
  if (id === state.lastScanValue && now - state.lastScanAt < 2500) return;
  state.lastScanValue = id;
  state.lastScanAt = now;
  const date = $('#scannerDate').value;
  const { volunteer, attendance } = await getVolunteerRecord(id, date);
  if (!volunteer) {
    state.selectedVolunteer = null;
    $('#scanResultCard').classList.add('hidden');
    hideScannerLookupResults();
    setScannerMessage(`Volunteer not found: ${id}. Check the QR code or add the volunteer first.`, 'error');
    navigator.vibrate?.([120, 60, 120]);
    return;
  }
  hideScannerLookupResults();
  state.selectedVolunteer = volunteer;
  renderScanResult(volunteer, attendance, date);
  navigator.vibrate?.(80);
}

function renderScanResult(volunteer, attendance, date) {
  const avatar = $('#resultAvatar');
  avatar.innerHTML = volunteer.photo ? `<img src="${escapeHtml(volunteer.photo)}" alt="" />` : escapeHtml(initials(volunteer.name));
  $('#resultName').textContent = volunteer.name;
  $('#resultId').textContent = volunteer.id;
  $('#resultTeam').textContent = volunteer.team || 'No team';
  const status = $('#resultStatus');
  status.className = `status-chip ${statusClass(attendance)}`;
  status.textContent = statusLabel(attendance);
  let previous = 'No attendance record for this date.';
  if (attendance?.status === 'checked_in') previous = `Checked in at ${displayTime(attendance.checkIn)}${attendance.late ? ' · Late arrival' : ''}.`;
  if (attendance?.status === 'checked_out') previous = `Checked in ${displayTime(attendance.checkIn)} · Checked out ${displayTime(attendance.checkOut)} · ${displayDuration(attendance.totalHours)}.`;
  if (attendance?.status === 'absent') previous = 'Marked absent. Use manual attendance to correct this record.';
  $('#resultPrevious').textContent = previous;
  $('#checkInBtn').disabled = Boolean(attendance && ['checked_in', 'checked_out', 'absent'].includes(attendance.status));
  $('#checkOutBtn').disabled = !attendance || attendance.status !== 'checked_in';
  $('#scanResultCard').classList.remove('hidden');
  if (!attendance) setScannerMessage(`${volunteer.name} found. Ready to check in.`, 'info');
  else if (attendance.status === 'checked_in') setScannerMessage(`${volunteer.name} is already checked in. Use Check Out when leaving.`, 'warning');
  else if (attendance.status === 'checked_out') setScannerMessage(`${volunteer.name} is already checked out for ${displayDate(date)}.`, 'warning');
  else setScannerMessage(`${volunteer.name} is marked absent. Manual correction is required.`, 'warning');
}

async function checkInVolunteer(volunteer, date, source = 'scanner') {
  const key = recordKey(date, volunteer.id);
  const existing = await db.attendance.get(key);
  if (existing?.status === 'checked_in') {
    setScannerMessage(`${volunteer.name} is already checked in at ${displayTime(existing.checkIn)}.`, 'warning');
    return;
  }
  if (existing?.status === 'checked_out') {
    setScannerMessage(`${volunteer.name} is already checked out. Duplicate check-in prevented.`, 'error');
    return;
  }
  if (existing?.status === 'absent') {
    toast('This volunteer is marked absent. Use manual attendance to correct the record.', 'warning');
    return;
  }
  const checkIn = nowForCampaignDate(date);
  const record = {
    key,
    volunteerId: volunteer.id,
    volunteerName: volunteer.name,
    date,
    checkIn,
    checkOut: '',
    totalHours: null,
    status: 'checked_in',
    late: isLate(checkIn, date),
    notes: '',
    source,
    lastModified: isoNow()
  };
  await db.attendance.put(record);
  await queueEvent('attendance_upsert', record);
  if (source === 'scanner') {
    state.selectedVolunteer = volunteer;
    renderScanResult(volunteer, record, date);
    setScannerMessage(`${volunteer.name} checked in successfully at ${displayTime(checkIn)}${record.late ? ' · Late arrival' : ''}.`, 'success');
  }
  toast('Check-in saved locally.', 'success');
  refreshVisibleViews();
}

async function checkOutVolunteer(volunteer, date, source = 'scanner') {
  const key = recordKey(date, volunteer.id);
  const existing = await db.attendance.get(key);
  if (!existing) {
    setScannerMessage(`${volunteer.name} has not checked in. Check-out was prevented.`, 'error');
    return;
  }
  if (existing.status === 'checked_out') {
    setScannerMessage(`${volunteer.name} is already checked out at ${displayTime(existing.checkOut)}.`, 'warning');
    return;
  }
  if (existing.status !== 'checked_in') {
    toast('Check-out is not allowed for this attendance status.', 'error');
    return;
  }
  const checkOut = nowForCampaignDate(date);
  const record = {
    ...existing,
    checkOut,
    totalHours: calcHours(existing.checkIn, checkOut),
    status: 'checked_out',
    source,
    lastModified: isoNow()
  };
  await db.attendance.put(record);
  await queueEvent('attendance_upsert', record);
  if (source === 'scanner') {
    state.selectedVolunteer = volunteer;
    renderScanResult(volunteer, record, date);
    setScannerMessage(`${volunteer.name} checked out successfully at ${displayTime(checkOut)} · ${displayDuration(record.totalHours)}.`, 'success');
  }
  toast('Check-out saved locally.', 'success');
  refreshVisibleViews();
}

function clearScanResult() {
  state.selectedVolunteer = null;
  $('#scanResultCard').classList.add('hidden');
  $('#manualScanId').value = '';
  hideScannerLookupResults();
  setScannerMessage('Ready to scan.', 'info');
}

async function startCamera() {
  if (state.scanning) return;
  if (!window.isSecureContext && location.hostname !== 'localhost') {
    setScannerMessage('Camera access requires HTTPS or localhost. Use “Scan image” or enter the volunteer ID.', 'error');
    return;
  }
  state.scanner ||= new Html5Qrcode('qrReader', { verbose: false });
  try {
    $('#cameraPlaceholder').classList.add('hidden');
    await state.scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: (w, h) => ({ width: Math.min(280, w * .72), height: Math.min(280, h * .72) }), aspectRatio: 1 },
      decoded => handleScannedValue(decoded),
      () => {}
    );
    state.scanning = true;
    $('#startCameraBtn').classList.add('hidden');
    $('#stopCameraBtn').classList.remove('hidden');
    setScannerMessage('Camera active. Place the volunteer QR code inside the frame.', 'info');
  } catch (error) {
    $('#cameraPlaceholder').classList.remove('hidden');
    setScannerMessage(`Could not start the camera: ${error?.message || error}`, 'error');
  }
}

async function stopCamera() {
  if (!state.scanner || !state.scanning) return;
  try { await state.scanner.stop(); } catch (_) {}
  state.scanning = false;
  $('#startCameraBtn').classList.remove('hidden');
  $('#stopCameraBtn').classList.add('hidden');
  $('#cameraPlaceholder').classList.remove('hidden');
}

async function scanImage(file) {
  if (!file) return;
  try {
    if (state.scanning) await stopCamera();
    state.scanner ||= new Html5Qrcode('qrReader', { verbose: false });
    const decoded = await state.scanner.scanFile(file, true);
    await handleScannedValue(decoded);
  } catch (error) {
    setScannerMessage('No valid volunteer QR code was found in that image.', 'error');
  } finally {
    $('#qrImageInput').value = '';
  }
}

async function renderDashboard() {
  const date = $('#dashboardDate').value;
  const [volunteers, records] = await Promise.all([db.volunteers.toArray(), db.attendance.where('date').equals(date).toArray()]);
  const map = new Map(records.map(r => [r.volunteerId, r]));
  $('#statTotal').textContent = String(volunteers.length);
  $('#statIn').textContent = String(records.filter(r => r.status === 'checked_in').length);
  $('#statOut').textContent = String(records.filter(r => r.status === 'checked_out').length);
  $('#statAbsent').textContent = String(volunteers.filter(v => !map.has(v.id) || map.get(v.id)?.status === 'absent').length);
  $('#statLate').textContent = String(records.filter(r => r.late && r.status !== 'absent').length);

  const search = $('#dashboardSearch').value.trim().toLowerCase();
  const team = $('#dashboardTeam').value;
  const filterStatus = $('#dashboardStatus').value;
  const rows = volunteers.map(v => ({ volunteer: v, record: map.get(v.id) || null })).filter(({ volunteer, record }) => {
    const textMatch = !search || `${volunteer.id} ${volunteer.name} ${volunteer.team}`.toLowerCase().includes(search);
    const teamMatch = !team || volunteer.team === team;
    let statusMatch = true;
    if (filterStatus === 'present') statusMatch = Boolean(record && ['checked_in', 'checked_out'].includes(record.status));
    if (filterStatus === 'checked_in') statusMatch = record?.status === 'checked_in';
    if (filterStatus === 'checked_out') statusMatch = record?.status === 'checked_out';
    if (filterStatus === 'absent') statusMatch = !record || record.status === 'absent';
    if (filterStatus === 'late') statusMatch = Boolean(record?.late);
    return textMatch && teamMatch && statusMatch;
  }).sort((a, b) => a.volunteer.name.localeCompare(b.volunteer.name));

  $('#dashboardTableBody').innerHTML = rows.map(({ volunteer, record }) => `
    <tr>
      <td><strong>${escapeHtml(volunteer.id)}</strong></td>
      <td><div class="table-person">${avatarHtml(volunteer)}<div><strong>${escapeHtml(volunteer.name)}</strong><small>${escapeHtml(volunteer.phone || '')}</small></div></div></td>
      <td>${escapeHtml(volunteer.team || '—')}</td>
      <td>${escapeHtml(displayTime(record?.checkIn))}</td>
      <td>${escapeHtml(displayTime(record?.checkOut))}</td>
      <td>${escapeHtml(displayDuration(record?.totalHours))}</td>
      <td><span class="status-chip ${statusClass(record)}">${escapeHtml(statusLabel(record))}</span></td>
      <td><button class="btn mini secondary edit-attendance" data-id="${escapeHtml(volunteer.id)}" data-date="${escapeHtml(date)}">Edit</button></td>
    </tr>`).join('');
  $('#dashboardEmpty').classList.toggle('hidden', rows.length > 0);
}

async function renderManualResults() {
  const search = $('#manualSearch').value.trim().toLowerCase();
  const date = $('#manualDate').value;
  let volunteers = await db.volunteers.toArray();
  volunteers = volunteers.filter(v => !search || `${v.id} ${v.name} ${v.team}`.toLowerCase().includes(search)).sort((a, b) => a.name.localeCompare(b.name)).slice(0, search ? 60 : 20);
  const records = await db.attendance.where('date').equals(date).toArray();
  const map = new Map(records.map(r => [r.volunteerId, r]));
  $('#manualResults').innerHTML = volunteers.length ? volunteers.map(v => {
    const r = map.get(v.id);
    return `<article class="manual-card">
      <div class="manual-card-info">${avatarHtml(v)}<div><strong>${escapeHtml(v.name)}</strong><div class="muted">${escapeHtml(v.id)} · ${escapeHtml(v.team || 'No team')}</div><div style="margin-top:6px"><span class="status-chip ${statusClass(r)}">${escapeHtml(statusLabel(r))}</span></div></div></div>
      <div class="manual-card-actions">
        <button class="btn success manual-checkin" data-id="${escapeHtml(v.id)}" ${r ? 'disabled' : ''}>Check In</button>
        <button class="btn danger manual-checkout" data-id="${escapeHtml(v.id)}" ${r?.status === 'checked_in' ? '' : 'disabled'}>Check Out</button>
        <button class="btn secondary edit-attendance" data-id="${escapeHtml(v.id)}" data-date="${escapeHtml(date)}">Edit</button>
        <button class="btn secondary mark-absent" data-id="${escapeHtml(v.id)}">Mark Absent</button>
      </div>
    </article>`;
  }).join('') : '<div class="empty-state">No volunteers match your search.</div>';
}

async function markAbsent(volunteer, date) {
  const existing = await db.attendance.get(recordKey(date, volunteer.id));
  if (existing && !confirm(`${volunteer.name} already has an attendance record. Replace it with Absent?`)) return;
  await requirePin(async () => {
    const record = {
      key: recordKey(date, volunteer.id), volunteerId: volunteer.id, volunteerName: volunteer.name, date,
      checkIn: '', checkOut: '', totalHours: 0, status: 'absent', late: false,
      notes: existing?.notes || '', source: 'manual', lastModified: isoNow()
    };
    await db.attendance.put(record);
    await queueEvent('attendance_upsert', record);
    toast(`${volunteer.name} marked absent.`, 'success');
    renderManualResults();
    refreshVisibleViews();
  });
}

async function openAttendanceDialog(volunteerId, date) {
  const volunteer = await db.volunteers.get(volunteerId);
  if (!volunteer) return;
  const record = await db.attendance.get(recordKey(date, volunteerId));
  $('#attendanceDialogTitle').textContent = `${volunteer.name} · ${displayDate(date)}`;
  $('#attendanceVolunteerId').value = volunteerId;
  $('#attendanceDateInput').value = date;
  $('#attendanceStatusInput').value = record?.status || 'checked_in';
  $('#attendanceCheckInInput').value = dateToTime(record?.checkIn);
  $('#attendanceCheckOutInput').value = dateToTime(record?.checkOut);
  $('#attendanceNotesInput').value = record?.notes || '';
  $('#resetAttendanceBtn').disabled = !record;
  $('#attendanceDialog').showModal();
}

async function saveAttendanceEdit() {
  const volunteerId = $('#attendanceVolunteerId').value;
  const date = $('#attendanceDateInput').value;
  const volunteer = await db.volunteers.get(volunteerId);
  if (!volunteer) throw new Error('Volunteer not found.');
  const status = $('#attendanceStatusInput').value;
  const inTime = $('#attendanceCheckInInput').value;
  const outTime = $('#attendanceCheckOutInput').value;
  let checkIn = inTime ? timeToDate(date, inTime) : '';
  let checkOut = outTime ? timeToDate(date, outTime) : '';
  if (status === 'checked_in' && !checkIn) throw new Error('Check-in time is required.');
  if (status === 'checked_out' && (!checkIn || !checkOut)) throw new Error('Both check-in and check-out times are required.');
  if (checkIn && checkOut && new Date(checkOut) < new Date(checkIn)) throw new Error('Check-out time cannot be earlier than check-in time.');
  if (status === 'absent') { checkIn = ''; checkOut = ''; }
  const record = {
    key: recordKey(date, volunteerId), volunteerId, volunteerName: volunteer.name, date,
    checkIn, checkOut,
    totalHours: status === 'checked_out' ? calcHours(checkIn, checkOut) : status === 'absent' ? 0 : null,
    status,
    late: status !== 'absent' && isLate(checkIn, date),
    notes: $('#attendanceNotesInput').value.trim(),
    source: 'manual_edit',
    lastModified: isoNow()
  };
  await db.attendance.put(record);
  await queueEvent('attendance_upsert', record);
  $('#attendanceDialog').close();
  toast('Attendance record updated.', 'success');
  refreshVisibleViews();
}

async function resetAttendanceRecord() {
  const volunteerId = $('#attendanceVolunteerId').value;
  const date = $('#attendanceDateInput').value;
  const volunteer = await db.volunteers.get(volunteerId);
  if (!confirm(`Reset the attendance record for ${volunteer?.name || volunteerId} on ${displayDate(date)}?`)) return;
  await requirePin(async () => {
    await db.attendance.delete(recordKey(date, volunteerId));
    await queueEvent('attendance_delete', { key: recordKey(date, volunteerId), volunteerId, date, deletedAt: isoNow() });
    $('#attendanceDialog').close();
    toast('Attendance record reset.', 'success');
    refreshVisibleViews();
  });
}

async function renderVolunteers() {
  const search = $('#volunteerSearch').value.trim().toLowerCase();
  const team = $('#volunteerTeam').value;
  let volunteers = await db.volunteers.toArray();
  volunteers = volunteers.filter(v => (!search || `${v.id} ${v.name} ${v.team} ${v.phone}`.toLowerCase().includes(search)) && (!team || v.team === team)).sort((a, b) => a.name.localeCompare(b.name));
  $('#volunteerTableBody').innerHTML = volunteers.map(v => `
    <tr>
      <td><input class="volunteer-select" type="checkbox" value="${escapeHtml(v.id)}" aria-label="Select ${escapeHtml(v.name)}" /></td>
      <td><strong>${escapeHtml(v.id)}</strong></td>
      <td><div class="table-person">${avatarHtml(v)}<div><strong>${escapeHtml(v.name)}</strong><small>${escapeHtml(v.notes || '')}</small></div></div></td>
      <td>${escapeHtml(v.team || '—')}</td>
      <td>${escapeHtml(v.phone || '—')}</td>
      <td><button class="btn mini secondary show-qr" data-id="${escapeHtml(v.id)}">View QR</button></td>
      <td><div class="table-actions"><button class="btn mini secondary edit-volunteer" data-id="${escapeHtml(v.id)}">Edit</button><button class="btn mini danger delete-volunteer" data-id="${escapeHtml(v.id)}">Delete</button></div></td>
    </tr>`).join('');
  $('#volunteersEmpty').classList.toggle('hidden', volunteers.length > 0);
}

function openVolunteerDialog(volunteer = null) {
  $('#volunteerDialogTitle').textContent = volunteer ? 'Edit volunteer' : 'Add volunteer';
  $('#volunteerOriginalId').value = volunteer?.id || '';
  $('#volunteerIdInput').value = volunteer?.id || randomId();
  $('#volunteerNameInput').value = volunteer?.name || '';
  $('#volunteerTeamInput').value = volunteer?.team || '';
  $('#volunteerPhoneInput').value = volunteer?.phone || '';
  $('#volunteerNotesInput').value = volunteer?.notes || '';
  $('#volunteerPhotoInput').value = '';
  state.editingVolunteerPhoto = volunteer?.photo || '';
  $('#photoFileName').textContent = volunteer?.photo ? 'Existing photo will be kept unless a new photo is selected.' : '';
  $('#volunteerDialog').showModal();
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function saveVolunteer() {
  const originalId = normalizeId($('#volunteerOriginalId').value);
  const id = normalizeId($('#volunteerIdInput').value);
  const name = $('#volunteerNameInput').value.trim();
  const team = $('#volunteerTeamInput').value.trim();
  if (!id || !name || !team) throw new Error('Volunteer ID, full name and team/role are required.');
  const duplicate = await db.volunteers.get(id);
  if (duplicate && id !== originalId) throw new Error(`Volunteer ID ${id} already exists.`);
  const file = $('#volunteerPhotoInput').files[0];
  const photo = file ? await fileToDataUrl(file) : state.editingVolunteerPhoto;
  const volunteer = {
    id, name, team,
    phone: $('#volunteerPhoneInput').value.trim(),
    notes: $('#volunteerNotesInput').value.trim(),
    photo,
    createdAt: duplicate?.createdAt || isoNow(),
    updatedAt: isoNow()
  };
  await requirePin(async () => {
    await db.transaction('rw', db.volunteers, db.attendance, async () => {
      if (originalId && originalId !== id) {
        const oldRecords = await db.attendance.where('volunteerId').equals(originalId).toArray();
        await db.volunteers.delete(originalId);
        await db.attendance.bulkDelete(oldRecords.map(r => r.key));
        if (oldRecords.length) await db.attendance.bulkPut(oldRecords.map(r => ({ ...r, key: recordKey(r.date, id), volunteerId: id, volunteerName: name, lastModified: isoNow() })));
      }
      await db.volunteers.put(volunteer);
    });
    await queueEvent('volunteer_upsert', volunteer);
    $('#volunteerDialog').close();
    toast(originalId ? 'Volunteer updated.' : 'Volunteer added.', 'success');
    await refreshTeamOptions();
    renderVolunteers();
    refreshVisibleViews();
  });
}

async function deleteVolunteer(id) {
  const volunteer = await db.volunteers.get(id);
  if (!volunteer) return;
  const records = await db.attendance.where('volunteerId').equals(id).count();
  if (!confirm(`Delete ${volunteer.name}? This will also delete ${records} attendance record${records === 1 ? '' : 's'}.`)) return;
  await requirePin(async () => {
    await db.transaction('rw', db.volunteers, db.attendance, async () => {
      const attendance = await db.attendance.where('volunteerId').equals(id).toArray();
      await db.attendance.bulkDelete(attendance.map(r => r.key));
      await db.volunteers.delete(id);
    });
    await queueEvent('volunteer_delete', { id, deletedAt: isoNow() });
    toast('Volunteer deleted.', 'success');
    await refreshTeamOptions();
    renderVolunteers();
    refreshVisibleViews();
  });
}

async function showQr(id) {
  const volunteer = await db.volunteers.get(id);
  if (!volunteer) return;
  const dataUrl = await QRCode.toDataURL(volunteer.id, { width: 800, margin: 2, errorCorrectionLevel: 'M' });
  state.currentQrVolunteer = { volunteer, dataUrl };
  $('#qrDialogName').textContent = volunteer.name;
  $('#qrDialogId').textContent = volunteer.id;
  $('#qrDialogTeam').textContent = volunteer.team || '';
  $('#qrDialogImage').src = dataUrl;
  $('#qrDialog').showModal();
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

function badgeHtml(volunteer, qrDataUrl) {
  return `<article class="badge">
    <img class="brand-logo" src="assets/beyonduni-logo.png" alt="BeyondUni" />
    <div class="badge-title">Campaign Volunteer</div>
    <img class="badge-qr" src="${qrDataUrl}" alt="QR code" />
    <h2>${escapeHtml(volunteer.name)}</h2>
    <strong>${escapeHtml(volunteer.id)}</strong>
    <p>${escapeHtml(volunteer.team || '')}</p>
  </article>`;
}

function openPrintWindow(title, content, extraCss = '') {
  const win = window.open('', '_blank');
  if (win) win.opener = null;
  if (!win) { toast('Popup blocked. Allow popups to print.', 'error'); return; }
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    *{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:24px;color:#111}.brand-logo{width:170px;height:70px;object-fit:contain;background:#080b10;padding:8px;border-radius:8px}.badge{width:86mm;height:54mm;border:1px solid #bbb;border-radius:5mm;padding:5mm;display:grid;grid-template-columns:1fr 35mm;grid-template-rows:auto auto 1fr auto;gap:2mm;break-inside:avoid;page-break-inside:avoid}.badge .brand-logo{width:45mm;height:16mm;grid-column:1}.badge-title{font-size:10pt;font-weight:bold;align-self:center}.badge-qr{grid-column:2;grid-row:1/5;width:34mm;height:34mm;align-self:center}.badge h2{margin:2mm 0 0;font-size:15pt}.badge strong{font-size:11pt}.badge p{margin:0;color:#555}.badges{display:grid;grid-template-columns:repeat(2,86mm);gap:8mm}.report-logo{width:190px;background:#080b10;padding:8px;border-radius:8px}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{border:1px solid #bbb;padding:7px;text-align:left;font-size:10pt}th{background:#eee}.report-meta{margin:12px 0;color:#444}.summary{display:flex;gap:20px;flex-wrap:wrap;margin:14px 0}.summary strong{display:block;font-size:18pt}${extraCss}
    @page{size:A4;margin:12mm}@media print{body{margin:0}}
  </style></head><body>${content}<script>window.onload=()=>setTimeout(()=>window.print(),250)<\/script></body></html>`);
  win.document.close();
}

function printCurrentQr() {
  if (!state.currentQrVolunteer) return;
  const { volunteer, dataUrl } = state.currentQrVolunteer;
  openPrintWindow(`${volunteer.name} badge`, `<div class="badges">${badgeHtml(volunteer, dataUrl)}</div>`);
}

async function printSelectedBadges() {
  const ids = $$('.volunteer-select:checked').map(el => el.value);
  if (!ids.length) { toast('Select at least one volunteer.', 'warning'); return; }
  const volunteers = await db.volunteers.bulkGet(ids);
  const badges = [];
  for (const volunteer of volunteers.filter(Boolean)) {
    const qr = await QRCode.toDataURL(volunteer.id, { width: 600, margin: 2 });
    badges.push(badgeHtml(volunteer, qr));
  }
  openPrintWindow('BeyondUni volunteer badges', `<div class="badges">${badges.join('')}</div>`);
}

function normalizeImportRow(row) {
  const normalized = {};
  for (const [key, value] of Object.entries(row)) normalized[String(key).trim().toLowerCase().replace(/[^a-z0-9]/g, '')] = value;
  const get = (...keys) => keys.map(k => normalized[k]).find(v => v !== undefined && v !== null && String(v).trim() !== '');
  return {
    id: normalizeId(get('volunteerid', 'id', 'code') || randomId()),
    name: String(get('fullname', 'name', 'volunteername') || '').trim(),
    team: String(get('team', 'role', 'teamorrole') || '').trim(),
    phone: String(get('phone', 'phonenumber', 'mobile') || '').trim(),
    notes: String(get('notes', 'note', 'comments') || '').trim()
  };
}

async function importVolunteers(file) {
  if (!file) return;
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: '' });
    const imported = rows.map(normalizeImportRow).filter(v => v.name && v.team);
    if (!imported.length) throw new Error('No valid rows found. Required columns: Full Name and Team/Role.');
    const duplicateIds = imported.map(v => v.id).filter((id, index, arr) => arr.indexOf(id) !== index);
    if (duplicateIds.length) throw new Error(`Duplicate volunteer IDs in file: ${[...new Set(duplicateIds)].slice(0, 5).join(', ')}`);
    await requirePin(async () => {
      const now = isoNow();
      const existing = await db.volunteers.bulkGet(imported.map(v => v.id));
      const volunteers = imported.map((v, i) => ({ ...v, photo: existing[i]?.photo || '', createdAt: existing[i]?.createdAt || now, updatedAt: now }));
      await db.volunteers.bulkPut(volunteers);
      for (const volunteer of volunteers) await queueEvent('volunteer_upsert', volunteer);
      toast(`${volunteers.length} volunteer${volunteers.length === 1 ? '' : 's'} imported.`, 'success');
      await refreshTeamOptions();
      renderVolunteers();
      refreshVisibleViews();
    });
  } catch (error) {
    toast(error.message || 'Could not import the file.', 'error');
  } finally {
    $('#volunteerImportInput').value = '';
  }
}

function downloadBlob(content, type, filename) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadTemplate() {
  const csv = 'Volunteer ID,Full Name,Team or Role,Phone Number,Notes\nBU-000001,Ahmed Ali,Registration,07700000000,\n';
  downloadBlob(csv, 'text/csv;charset=utf-8', 'BeyondUni-volunteer-import-template.csv');
}

async function exportVolunteers() {
  const volunteers = (await db.volunteers.toArray()).map(v => ({
    'Volunteer ID': v.id, 'Full Name': v.name, 'Team or Role': v.team, 'Phone Number': v.phone || '', Notes: v.notes || ''
  }));
  const ws = XLSX.utils.json_to_sheet(volunteers);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Volunteers');
  XLSX.writeFile(wb, `BeyondUni-volunteers-${todayIso()}.xlsx`);
}

async function buildReport() {
  const type = $('#reportType').value;
  const selectedDate = $('#reportDate').value;
  const team = $('#reportTeam').value;
  const search = $('#reportSearch').value.trim().toLowerCase();
  let volunteers = await db.volunteers.toArray();
  volunteers = volunteers.filter(v => (!team || v.team === team) && (!search || `${v.id} ${v.name}`.toLowerCase().includes(search))).sort((a, b) => a.name.localeCompare(b.name));
  const allRecords = await db.attendance.toArray();
  const dates = state.settings.campaignDates || [];
  let rows = [];
  let headers = [];
  if (type === 'date') {
    const map = new Map(allRecords.filter(r => r.date === selectedDate).map(r => [r.volunteerId, r]));
    rows = volunteers.map(v => {
      const r = map.get(v.id);
      return {
        'Volunteer ID': v.id,
        'Volunteer Name': v.name,
        'Team / Role': v.team,
        'Date': displayDate(selectedDate),
        'Check-in': displayTime(r?.checkIn),
        'Check-out': displayTime(r?.checkOut),
        'Total Hours': r?.totalHours == null ? '' : Number(r.totalHours.toFixed(2)),
        'Status': statusLabel(r),
        'Late': r?.late ? 'Yes' : 'No',
        'Notes': r?.notes || ''
      };
    });
    headers = Object.keys(rows[0] || { 'Volunteer ID': '', 'Volunteer Name': '', 'Team / Role': '', Date: '', 'Check-in': '', 'Check-out': '', 'Total Hours': '', Status: '', Late: '', Notes: '' });
  } else {
    rows = volunteers.map(v => {
      const records = allRecords.filter(r => r.volunteerId === v.id && dates.includes(r.date));
      const present = records.filter(r => ['checked_in', 'checked_out'].includes(r.status)).length;
      const hours = records.reduce((sum, r) => sum + Number(r.totalHours || 0), 0);
      const late = records.filter(r => r.late).length;
      const missing = records.filter(r => r.status === 'checked_in').length;
      return {
        'Volunteer ID': v.id,
        'Volunteer Name': v.name,
        'Team / Role': v.team,
        'Days Present': present,
        'Campaign Days': dates.length,
        'Attendance %': dates.length ? Number(((present / dates.length) * 100).toFixed(1)) : 0,
        'Total Hours': Number(hours.toFixed(2)),
        'Late Arrivals': late,
        'Missing Check-out': missing
      };
    });
    headers = Object.keys(rows[0] || { 'Volunteer ID': '', 'Volunteer Name': '', 'Team / Role': '', 'Days Present': '', 'Campaign Days': '', 'Attendance %': '', 'Total Hours': '', 'Late Arrivals': '', 'Missing Check-out': '' });
  }
  return { rows, headers, allRecords, volunteers };
}

async function renderReport() {
  const { rows, headers, allRecords, volunteers } = await buildReport();
  state.reportRows = rows;
  state.reportHeaders = headers;
  $('#reportTableHead').innerHTML = `<tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>`;
  $('#reportTableBody').innerHTML = rows.map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>`).join('');
  $('#reportEmpty').classList.toggle('hidden', rows.length > 0);
  const selectedDate = $('#reportDate').value;
  const filteredRecords = $('#reportType').value === 'date' ? allRecords.filter(r => r.date === selectedDate && volunteers.some(v => v.id === r.volunteerId)) : allRecords.filter(r => volunteers.some(v => v.id === r.volunteerId));
  const totalHours = filteredRecords.reduce((s, r) => s + Number(r.totalHours || 0), 0);
  const campaignDays = state.settings.campaignDates.length || 1;
  const presentCount = $('#reportType').value === 'date'
    ? filteredRecords.filter(r => ['checked_in', 'checked_out'].includes(r.status)).length
    : filteredRecords.filter(r => ['checked_in', 'checked_out'].includes(r.status)).length;
  const denominator = $('#reportType').value === 'date' ? volunteers.length : volunteers.length * campaignDays;
  $('#reportHours').textContent = displayDuration(totalHours);
  $('#reportRate').textContent = `${denominator ? Math.round((presentCount / denominator) * 100) : 0}%`;
  $('#reportLate').textContent = String(filteredRecords.filter(r => r.late).length);
  $('#reportMissingCheckout').textContent = String(filteredRecords.filter(r => r.status === 'checked_in').length);
  $('#reportDate').closest('.field').classList.toggle('hidden', $('#reportType').value !== 'date');
}

function exportReport(format) {
  if (!state.reportRows.length) { toast('There is no report data to export.', 'warning'); return; }
  const datePart = $('#reportType').value === 'date' ? $('#reportDate').value : 'all-days';
  if (format === 'csv') {
    const ws = XLSX.utils.json_to_sheet(state.reportRows);
    downloadBlob(XLSX.utils.sheet_to_csv(ws), 'text/csv;charset=utf-8', `BeyondUni-attendance-report-${datePart}.csv`);
  } else {
    const ws = XLSX.utils.json_to_sheet(state.reportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');
    XLSX.writeFile(wb, `BeyondUni-attendance-report-${datePart}.xlsx`);
  }
}

function printReport() {
  if (!state.reportRows.length) { toast('There is no report data to print.', 'warning'); return; }
  const headers = state.reportHeaders;
  const table = `<table><thead><tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${state.reportRows.map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  const dateText = $('#reportType').value === 'date' ? displayDate($('#reportDate').value) : 'All campaign days';
  const summary = `<div class="summary"><div><span>Total hours</span><strong>${escapeHtml($('#reportHours').textContent)}</strong></div><div><span>Attendance rate</span><strong>${escapeHtml($('#reportRate').textContent)}</strong></div><div><span>Late arrivals</span><strong>${escapeHtml($('#reportLate').textContent)}</strong></div><div><span>No check-out</span><strong>${escapeHtml($('#reportMissingCheckout').textContent)}</strong></div></div>`;
  openPrintWindow('BeyondUni attendance report', `<img class="report-logo" src="assets/beyonduni-logo.png" alt="BeyondUni"><h1>Attendance Report</h1><div class="report-meta">${escapeHtml(dateText)} · Generated ${escapeHtml(new Date().toLocaleString())}</div>${summary}${table}`);
}

async function createBackupObject() {
  return {
    app: 'BeyondUni Attendance Tracker',
    version: 1,
    exportedAt: isoNow(),
    volunteers: await db.volunteers.toArray(),
    attendance: await db.attendance.toArray(),
    settings: await db.settings.toArray(),
    outbox: await db.outbox.toArray()
  };
}

async function createLocalSnapshot() {
  const data = await createBackupObject();
  const id = await db.backups.add({ createdAt: isoNow(), data });
  const all = await db.backups.orderBy('createdAt').reverse().toArray();
  if (all.length > 10) await db.backups.bulkDelete(all.slice(10).map(x => x.id));
  await settingSet('lastBackupAt', data.exportedAt);
  renderBackupStatus();
  return id;
}

async function maybeAutoBackup(force = false) {
  const last = state.settings.lastBackupAt ? new Date(state.settings.lastBackupAt).getTime() : 0;
  if (force || Date.now() - last > 5 * 60 * 1000) await createLocalSnapshot();
}

async function downloadBackup() {
  const data = await createBackupObject();
  downloadBlob(JSON.stringify(data, null, 2), 'application/json', `BeyondUni-complete-backup-${todayIso()}-${Date.now()}.json`);
  await createLocalSnapshot();
  toast('Complete backup downloaded.', 'success');
}

function validateBackup(data) {
  if (!data || data.app !== 'BeyondUni Attendance Tracker' || !Array.isArray(data.volunteers) || !Array.isArray(data.attendance) || !Array.isArray(data.settings)) {
    throw new Error('This is not a valid BeyondUni attendance backup.');
  }
}

async function restoreBackupData(data) {
  validateBackup(data);
  await db.transaction('rw', db.volunteers, db.attendance, db.settings, db.outbox, async () => {
    await Promise.all([db.volunteers.clear(), db.attendance.clear(), db.settings.clear(), db.outbox.clear()]);
    if (data.volunteers.length) await db.volunteers.bulkPut(data.volunteers);
    if (data.attendance.length) await db.attendance.bulkPut(data.attendance);
    if (data.settings.length) await db.settings.bulkPut(data.settings);
    if (data.outbox?.length) await db.outbox.bulkPut(data.outbox);
  });
  await loadSettings();
  populateDateSelects();
  await refreshTeamOptions();
  renderSettings();
  refreshVisibleViews();
}

async function restoreBackupFile(file) {
  if (!file) return;
  try {
    const data = JSON.parse(await file.text());
    validateBackup(data);
    if (!confirm(`Restore backup from ${new Date(data.exportedAt).toLocaleString()}? Current data will be replaced.`)) return;
    await requirePin(async () => {
      await createLocalSnapshot();
      await restoreBackupData(data);
      toast('Backup restored successfully.', 'success');
    });
  } catch (error) {
    toast(error.message || 'Could not restore backup.', 'error');
  } finally {
    $('#restoreBackupInput').value = '';
  }
}

async function restoreLatestSnapshot() {
  const latest = await db.backups.orderBy('createdAt').last();
  if (!latest) { toast('No local snapshot is available.', 'warning'); return; }
  if (!confirm(`Restore the local snapshot from ${new Date(latest.createdAt).toLocaleString()}? Current data will be replaced.`)) return;
  await requirePin(async () => {
    await restoreBackupData(latest.data);
    toast('Latest local snapshot restored.', 'success');
  });
}

async function renderBackupStatus() {
  const latest = await db.backups.orderBy('createdAt').last();
  $('#backupStatus').textContent = latest ? `Latest automatic local snapshot: ${new Date(latest.createdAt).toLocaleString()}` : 'No local snapshot yet.';
}

function renderSettings() {
  $('#campaignDatesInput').value = (state.settings.campaignDates || []).join('\n');
  $('#campaignStartInput').value = state.settings.campaignStart || '16:00';
  $('#lateThresholdInput').value = state.settings.lateThreshold ?? 15;
  $('#newPinInput').value = '';
  $('#newPinInput').placeholder = state.settings.pinHash ? 'PIN enabled · enter a new PIN or leave blank to disable' : 'Leave blank to disable';
  $('#syncEndpointInput').value = state.settings.syncEndpoint || '';
  const base = `${location.origin}${location.pathname}`;
  $('#privateAdminLink').value = `${base}#supervisor-${state.settings.adminToken}`;
  renderBackupStatus();
  syncOutbox(false);
}

async function saveCampaignSettings() {
  const dates = $('#campaignDatesInput').value.split(/\n|,/).map(x => x.trim()).filter(Boolean);
  if (!dates.length || dates.some(d => !/^\d{4}-\d{2}-\d{2}$/.test(d))) throw new Error('Enter at least one valid date in YYYY-MM-DD format.');
  const start = $('#campaignStartInput').value;
  const threshold = Number($('#lateThresholdInput').value);
  if (!start) throw new Error('Campaign start time is required.');
  if (!Number.isFinite(threshold) || threshold < 0 || threshold > 240) throw new Error('Late threshold must be between 0 and 240 minutes.');
  await requirePin(async () => {
    await settingSet('campaignDates', [...new Set(dates)].sort());
    await settingSet('campaignStart', start);
    await settingSet('lateThreshold', threshold);
    populateDateSelects();
    await queueEvent('settings_update', { campaignDates: state.settings.campaignDates, campaignStart: start, lateThreshold: threshold, updatedAt: isoNow() });
    toast('Campaign settings saved.', 'success');
  });
}

async function savePin() {
  const value = $('#newPinInput').value.trim();
  if (value && !/^\d{4,12}$/.test(value)) throw new Error('PIN must contain 4–12 digits.');
  const newHash = value ? await sha256(value) : '';
  if (state.settings.pinHash) {
    await requirePin(async () => {
      await settingSet('pinHash', newHash);
      $('#newPinInput').value = '';
      toast(value ? 'Device PIN changed.' : 'Device PIN disabled.', 'success');
      renderSettings();
    });
  } else {
    await settingSet('pinHash', newHash);
    $('#newPinInput').value = '';
    toast(value ? 'Device PIN enabled.' : 'Device PIN remains disabled.', 'success');
    renderSettings();
  }
}

async function saveSyncEndpoint() {
  const endpoint = $('#syncEndpointInput').value.trim();
  if (endpoint) {
    try { new URL(endpoint); } catch { throw new Error('Enter a valid HTTPS sync endpoint URL.'); }
    if (!endpoint.startsWith('https://') && !endpoint.startsWith('http://localhost')) throw new Error('Use an HTTPS endpoint for data protection.');
  }
  await requirePin(async () => {
    await settingSet('syncEndpoint', endpoint);
    setConnectionBadge();
    toast(endpoint ? 'Sync endpoint saved.' : 'Sync endpoint removed. Local-only mode is active.', 'success');
    syncOutbox(false);
  });
}

async function resetAllData() {
  if (!confirm('Permanently delete ALL volunteers, attendance records, backups and settings from this device?')) return;
  const phrase = prompt('Type DELETE ALL to confirm:');
  if (phrase !== 'DELETE ALL') { toast('Reset cancelled.', 'warning'); return; }
  await requirePin(async () => {
    await db.delete();
    location.reload();
  });
}

function refreshVisibleViews() {
  if (state.currentPage === 'dashboard') renderDashboard();
  if (state.currentPage === 'manual') renderManualResults();
  if (state.currentPage === 'volunteers') renderVolunteers();
  if (state.currentPage === 'reports') renderReport();
}

function bindEvents() {
  $$('.nav-btn').forEach(btn => btn.addEventListener('click', () => navigate(btn.dataset.page)));
  let logoTapCount = 0;
  let logoTapTimer;
  $('#brandUnlock').addEventListener('click', () => {
    if (state.supervisorUnlocked) return navigate('scanner');
    logoTapCount++;
    clearTimeout(logoTapTimer);
    logoTapTimer = setTimeout(() => { logoTapCount = 0; }, 1800);
    if (logoTapCount >= 5) { logoTapCount = 0; unlockSupervisor(); }
  });
  $('#lockSupervisorBtn').addEventListener('click', lockSupervisor);

  $('#scannerDate').addEventListener('change', async () => {
    updateCampaignDateLabel();
    clearScanResult();
  });
  $('#startCameraBtn').addEventListener('click', startCamera);
  $('#stopCameraBtn').addEventListener('click', stopCamera);
  $('#qrImageInput').addEventListener('change', e => scanImage(e.target.files[0]));
  $('#manualScanBtn').addEventListener('click', () => handleManualVolunteerLookup($('#manualScanId').value));
  $('#manualScanId').addEventListener('keydown', e => { if (e.key === 'Enter') handleManualVolunteerLookup(e.target.value); });
  $('#manualScanId').addEventListener('input', e => { if (!e.target.value.trim()) hideScannerLookupResults(); });
  $('#scannerLookupResults').addEventListener('click', async e => {
    const option = e.target.closest('button[data-volunteer-id]');
    if (!option) return;
    const volunteer = await db.volunteers.get(option.dataset.volunteerId);
    if (volunteer) await selectVolunteerForScanner(volunteer, volunteer.name);
  });
  $('#checkInBtn').addEventListener('click', () => state.selectedVolunteer && checkInVolunteer(state.selectedVolunteer, $('#scannerDate').value));
  $('#checkOutBtn').addEventListener('click', () => state.selectedVolunteer && checkOutVolunteer(state.selectedVolunteer, $('#scannerDate').value));
  $('#cancelScanBtn').addEventListener('click', clearScanResult);

  ['dashboardDate', 'dashboardSearch', 'dashboardTeam', 'dashboardStatus'].forEach(id => $(`#${id}`).addEventListener(id.includes('Search') ? 'input' : 'change', renderDashboard));
  $('#dashboardRefreshBtn').addEventListener('click', renderDashboard);
  $('#manualDate').addEventListener('change', renderManualResults);
  $('#manualSearch').addEventListener('input', renderManualResults);
  $('#manualResults').addEventListener('click', async e => {
    const btn = e.target.closest('button[data-id]'); if (!btn) return;
    const volunteer = await db.volunteers.get(btn.dataset.id); if (!volunteer) return;
    const date = $('#manualDate').value;
    if (btn.classList.contains('manual-checkin')) checkInVolunteer(volunteer, date, 'manual');
    if (btn.classList.contains('manual-checkout')) checkOutVolunteer(volunteer, date, 'manual');
    if (btn.classList.contains('mark-absent')) markAbsent(volunteer, date);
  });

  document.addEventListener('click', e => {
    const editAttendance = e.target.closest('.edit-attendance');
    if (editAttendance) openAttendanceDialog(editAttendance.dataset.id, editAttendance.dataset.date);
  });
  $('#attendanceForm').addEventListener('submit', e => { e.preventDefault(); requirePin(async () => { try { await saveAttendanceEdit(); } catch (err) { toast(err.message, 'error'); } }); });
  $('#resetAttendanceBtn').addEventListener('click', resetAttendanceRecord);

  $('#addVolunteerBtn').addEventListener('click', () => openVolunteerDialog());
  $('#generateVolunteerIdBtn').addEventListener('click', () => { $('#volunteerIdInput').value = randomId(); });
  $('#volunteerPhotoInput').addEventListener('change', e => { $('#photoFileName').textContent = e.target.files[0]?.name || ''; });
  $('#volunteerForm').addEventListener('submit', e => { e.preventDefault(); saveVolunteer().catch(err => toast(err.message, 'error')); });
  $('#volunteerSearch').addEventListener('input', renderVolunteers);
  $('#volunteerTeam').addEventListener('change', renderVolunteers);
  $('#volunteerTableBody').addEventListener('click', async e => {
    const btn = e.target.closest('button[data-id]'); if (!btn) return;
    if (btn.classList.contains('show-qr')) showQr(btn.dataset.id);
    if (btn.classList.contains('edit-volunteer')) { const v = await db.volunteers.get(btn.dataset.id); openVolunteerDialog(v); }
    if (btn.classList.contains('delete-volunteer')) deleteVolunteer(btn.dataset.id);
  });
  $('#selectAllVolunteers').addEventListener('change', e => $$('.volunteer-select').forEach(cb => { cb.checked = e.target.checked; }));
  $('#volunteerImportInput').addEventListener('change', e => importVolunteers(e.target.files[0]));
  $('#downloadTemplateBtn').addEventListener('click', downloadTemplate);
  $('#exportVolunteersBtn').addEventListener('click', exportVolunteers);
  $('#printSelectedBadgesBtn').addEventListener('click', printSelectedBadges);
  $('#closeQrDialogBtn').addEventListener('click', () => $('#qrDialog').close());
  $('#downloadQrBtn').addEventListener('click', () => state.currentQrVolunteer && downloadDataUrl(state.currentQrVolunteer.dataUrl, `${state.currentQrVolunteer.volunteer.id}-QR.png`));
  $('#printQrBtn').addEventListener('click', printCurrentQr);

  ['reportType', 'reportDate', 'reportTeam'].forEach(id => $(`#${id}`).addEventListener('change', renderReport));
  $('#reportSearch').addEventListener('input', renderReport);
  $('#exportReportCsvBtn').addEventListener('click', () => exportReport('csv'));
  $('#exportReportXlsxBtn').addEventListener('click', () => exportReport('xlsx'));
  $('#printReportBtn').addEventListener('click', printReport);

  $('#saveCampaignSettingsBtn').addEventListener('click', () => saveCampaignSettings().catch(err => toast(err.message, 'error')));
  $('#savePinBtn').addEventListener('click', () => savePin().catch(err => toast(err.message, 'error')));
  $('#saveSyncEndpointBtn').addEventListener('click', () => saveSyncEndpoint().catch(err => toast(err.message, 'error')));
  $('#syncNowBtn').addEventListener('click', () => syncOutbox(true));
  $('#downloadBackupBtn').addEventListener('click', downloadBackup);
  $('#restoreBackupInput').addEventListener('change', e => restoreBackupFile(e.target.files[0]));
  $('#restoreLatestSnapshotBtn').addEventListener('click', restoreLatestSnapshot);
  $('#copyAdminLinkBtn').addEventListener('click', async () => {
    const value = $('#privateAdminLink').value;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else { $('#privateAdminLink').select(); document.execCommand('copy'); }
      toast('Private supervisor link copied.', 'success');
    } catch {
      $('#privateAdminLink').select();
      toast('Select and copy the private link manually.', 'warning');
    }
  });
  $('#resetAllDataBtn').addEventListener('click', resetAllData);

  $('#pinForm').addEventListener('submit', async e => {
    e.preventDefault();
    const hash = await sha256($('#pinInput').value);
    if (hash !== state.settings.pinHash) { $('#pinError').classList.remove('hidden'); return; }
    const action = state.pendingPinAction;
    state.pendingPinAction = null;
    $('#pinDialog').close();
    if (action) await action();
  });
  $('#pinDialog').addEventListener('close', () => { if ($('#pinDialog').returnValue === 'cancel') state.pendingPinAction = null; });

  window.addEventListener('online', () => { setConnectionBadge(); syncOutbox(false); });
  window.addEventListener('offline', setConnectionBadge);
  window.addEventListener('beforeunload', () => { if (state.scanning) stopCamera(); });

  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault(); state.deferredInstallPrompt = e; $('#installBtn').classList.remove('hidden');
  });
  $('#installBtn').addEventListener('click', async () => {
    if (!state.deferredInstallPrompt) return;
    state.deferredInstallPrompt.prompt();
    await state.deferredInstallPrompt.userChoice;
    state.deferredInstallPrompt = null;
    $('#installBtn').classList.add('hidden');
  });
}

async function init() {
  await loadSettings();
  bindEvents();
  populateDateSelects();
  await refreshTeamOptions();
  setConnectionBadge();
  const expectedHash = `#supervisor-${state.settings.adminToken}`;
  if (location.hash === expectedHash) unlockSupervisor(false);
  navigate('scanner');
  await maybeAutoBackup(false);
  state.backupTimer = setInterval(() => maybeAutoBackup(false), 5 * 60 * 1000);
  if (navigator.onLine && state.settings.syncEndpoint) await syncOutbox(false);
  if ('serviceWorker' in navigator && location.protocol !== 'file:') {
    navigator.serviceWorker.register('./service-worker.js').catch(() => {});
  }
}

init().catch(error => {
  console.error(error);
  document.body.innerHTML = `<main style="max-width:720px;margin:40px auto;padding:20px;font-family:sans-serif"><h1>BeyondUni Attendance Tracker</h1><p>The application could not start.</p><pre>${escapeHtml(error.stack || error.message || error)}</pre></main>`;
});
